import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { Sun, Moon, Menu, X, Volume2 } from 'lucide-react';
import { buildingInPublicTasks } from './BuildingInPublicPage';

// Soundboard audio
const soundboardAudio = "https://customer-assets-jt897jd0.emergentagent.net/job_406c254c-0dbb-4785-b762-b2be521745e0/artifacts/d9985e73456ce3e4_jody-watley-want-me-darrell-freeman.mp3";

const TopNav = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const menuRef = useRef(null);
  const audioRef = useRef(null);
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();

  // Soundboard function - plays/pauses the audio
  const playSound = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio(soundboardAudio);
      audioRef.current.onended = () => setIsPlaying(false);
    }
    
    if (isPlaying) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      // Show nav only after scrolling past the fold (e.g., 300px)
      setIsVisible(window.scrollY > 300);
      
      // Calculate scroll progress
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const navItems = [
    { id: 'home', label: 'Home', path: '/' },
    { id: 'blog', label: 'Blog', path: '/blog' },
    { id: 'music', label: 'Music', path: '/music' },
    { id: 'media', label: 'Media', path: '/media' },
    { id: 'roadmap', label: 'Roadmap', path: '/roadmap' }
  ];

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <nav className={`top-nav ${isVisible ? 'visible' : ''}`} data-testid="top-nav">
      <div className="nav-inner">
        {/* Logo with heat shimmer animation */}
        <div className="nav-logo-container">
          <Link to="/" className="nav-logo" data-testid="nav-logo">
            <span className="logo-text">
              <span className="logo-main">Alan was&nbsp;</span>
              <span className="logo-accent">here</span>
              <span className="logo-main">.</span>
            </span>
          </Link>
          <Link to="/building-in-public" className="building-link" data-testid="building-in-public-link">
            Building in Public
          </Link>
        </div>

        {/* Center Nav Links */}
        <div className="nav-links">
          {navItems.map((item) => (
            <Link
              key={item.id}
              to={item.path}
              className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
              data-testid={`nav-link-${item.id}`}
            >
              {item.label}
            </Link>
          ))}
        </div>

        {/* Right Side - CTA + Menu + Theme Toggle */}
        <div className="nav-actions">
          <Link to="/blog" className="nav-cta" data-testid="nav-cta">
            Read Blog
          </Link>
          
          {/* Menu Button with Dropdown */}
          <div className="menu-container" ref={menuRef}>
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)} 
              className="menu-toggle" 
              data-testid="menu-toggle"
              aria-label="Toggle menu"
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
            
            {/* Dropdown Menu */}
            <div className={`menu-dropdown ${isMenuOpen ? 'open' : ''}`}>
              {navItems.map((item) => (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`menu-item ${isActive(item.path) ? 'active' : ''}`}
                  data-testid={`menu-item-${item.id}`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
          
          <button 
            onClick={playSound}
            className={`soundboard-btn ${isPlaying ? 'speaking' : ''}`}
            data-testid="soundboard-btn"
            aria-label="Play sound"
            title="Click to play"
          >
            <Volume2 size={18} />
          </button>
          
          <button 
            onClick={toggleTheme} 
            className="theme-toggle" 
            data-testid="theme-toggle"
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDark ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </div>
      </div>
      
      {/* News Ticker */}
      <Link to="/building-in-public" className="news-ticker-container" data-testid="news-ticker">
        <div className="live-badge">
          <span className="live-dot" />
          <span className="live-text">EARNINGS</span>
        </div>
        <div className="ticker-wrapper">
          <div className="ticker-track">
            <div className="ticker-content">
              {buildingInPublicTasks.map((item) => (
                <span key={`a-${item.id}`} className="ticker-item">
                  <span className="ticker-time">{item.time}</span>
                  <span className="ticker-task">{item.task}</span>
                  <span className="ticker-separator">•</span>
                </span>
              ))}
            </div>
            <div className="ticker-content">
              {buildingInPublicTasks.map((item) => (
                <span key={`b-${item.id}`} className="ticker-item">
                  <span className="ticker-time">{item.time}</span>
                  <span className="ticker-task">{item.task}</span>
                  <span className="ticker-separator">•</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </Link>

      {/* Scroll Progress Bar - at the very bottom */}
      <div className="scroll-progress-container">
        <div 
          className="scroll-progress-bar" 
          style={{ width: `${scrollProgress}%` }}
          data-testid="scroll-progress"
        />
      </div>

      <style jsx>{`
        .top-nav {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          transform: translateY(-100%);
          opacity: 0;
          pointer-events: none;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          max-width: 100vw;
        }

        .top-nav.visible {
          transform: translateY(0);
          opacity: 1;
          pointer-events: auto;
        }

        .scroll-progress-container {
          position: relative;
          width: 100%;
          height: 4px;
          background: ${isDark ? 'rgba(100, 100, 100, 0.3)' : 'rgba(0, 0, 0, 0.1)'};
          z-index: 5;
        }

        .scroll-progress-bar {
          height: 100%;
          background: #f46c38;
          transition: width 0.1s ease-out;
        }

        .news-ticker-container {
          display: flex;
          align-items: center;
          background: ${isDark ? 'rgba(21, 19, 18, 0.98)' : 'rgba(248, 250, 252, 0.98)'};
          border-top: 1px solid ${isDark ? 'rgba(182, 180, 189, 0.08)' : 'rgba(0, 0, 0, 0.05)'};
          padding: 6px 0;
          overflow: hidden;
          text-decoration: none;
          cursor: pointer;
          transition: background-color 0.2s ease;
          max-width: 100vw;
          width: 100%;
          position: relative;
          z-index: 1;
        }

        .news-ticker-container:hover {
          background: ${isDark ? 'rgba(30, 28, 27, 0.98)' : 'rgba(241, 245, 249, 0.98)'};
        }

        .live-badge {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 4px 12px 4px 16px;
          border-right: 1px solid ${isDark ? 'rgba(182, 180, 189, 0.15)' : 'rgba(0, 0, 0, 0.08)'};
          flex-shrink: 0;
        }

        .live-dot {
          width: 6px;
          height: 6px;
          background: #f46c38;
          border-radius: 50%;
          animation: blink 1.5s infinite;
        }

        @keyframes blink {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.3;
          }
        }

        .live-text {
          font-family: 'Inter', sans-serif;
          font-size: 10px;
          font-weight: 700;
          color: #f46c38;
          letter-spacing: 0.08em;
        }

        .ticker-wrapper {
          flex: 1;
          overflow: hidden;
          mask-image: linear-gradient(to right, transparent, black 20px, black calc(100% - 20px), transparent);
          min-width: 0;
        }

        .ticker-track {
          display: flex;
          width: max-content;
          animation: tickerScroll 30s linear infinite;
          will-change: transform;
        }

        .ticker-track:hover {
          animation-play-state: paused;
        }

        .ticker-content {
          display: flex;
          white-space: nowrap;
          flex-shrink: 0;
        }

        @keyframes tickerScroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }

        .ticker-item {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 0 16px;
        }

        .ticker-time {
          font-family: 'Inter', monospace;
          font-size: 11px;
          font-weight: 500;
          color: ${isDark ? '#f46c38' : '#ea580c'};
        }

        .ticker-task {
          font-family: 'Poppins', sans-serif;
          font-size: 12px;
          font-weight: 400;
          color: ${isDark ? '#998f8f' : '#64748b'};
        }

        .ticker-separator {
          color: ${isDark ? 'rgba(182, 180, 189, 0.3)' : 'rgba(0, 0, 0, 0.2)'};
          font-size: 10px;
        }

        .nav-inner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          max-width: 1400px;
          margin: 0 auto;
          padding: 12px 32px;
          background: ${isDark ? 'rgba(21, 19, 18, 0.95)' : 'rgba(255, 255, 255, 0.95)'};
          backdrop-filter: blur(12px);
          border-bottom: 1px solid ${isDark ? 'rgba(182, 180, 189, 0.1)' : 'rgba(0, 0, 0, 0.08)'};
          position: relative;
          z-index: 10;
        }

        .nav-logo-container {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .nav-logo {
          text-decoration: none;
          display: flex;
          align-items: center;
          gap: 8px;
          position: relative;
        }

        .building-link {
          font-family: 'Inter', sans-serif;
          font-size: 11px;
          font-weight: 500;
          color: ${isDark ? '#64748b' : '#94a3b8'};
          text-decoration: none;
          padding: 4px 10px;
          border: 1px solid ${isDark ? 'rgba(100, 116, 139, 0.3)' : 'rgba(148, 163, 184, 0.4)'};
          border-radius: 4px;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .building-link:hover {
          color: #f46c38;
          border-color: rgba(244, 108, 56, 0.5);
          background: rgba(244, 108, 56, 0.05);
        }

        .logo-text {
          font-family: 'Inter', sans-serif;
          font-size: 18px;
          font-weight: 700;
          color: ${isDark ? '#fff' : '#151312'};
          letter-spacing: -0.5px;
          display: flex;
          align-items: center;
        }

        .logo-main {
          color: ${isDark ? '#fff' : '#151312'};
        }

        .logo-accent {
          color: #f46c38;
          animation: heatShimmer 3s ease-in-out infinite;
          display: inline-block;
        }

        @keyframes heatShimmer {
          0%, 100% {
            filter: blur(0px);
            transform: translateY(0) skewX(0deg);
          }
          25% {
            filter: blur(1.5px);
            transform: translateY(-2.5px) skewX(1.5deg);
          }
          50% {
            filter: blur(2.5px);
            transform: translateY(2.5px) skewX(-1.5deg);
          }
          75% {
            filter: blur(1.5px);
            transform: translateY(-1.5px) skewX(1deg);
          }
        }

        .nav-logo:hover .logo-accent,
        .nav-logo:active .logo-accent,
        .nav-logo:focus .logo-accent {
          animation: heatShimmer 3s ease-in-out infinite;
        }

        .logo-divider {
          color: ${isDark ? '#94a3b8' : '#64748b'};
          font-weight: 400;
          white-space: pre;
        }

        .nav-links {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .nav-link {
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          font-weight: 500;
          color: ${isDark ? '#94a3b8' : '#64748b'};
          text-decoration: none;
          padding: 8px 16px;
          border-radius: 6px;
          transition: all 0.2s ease;
        }

        .nav-link:hover {
          color: ${isDark ? '#fff' : '#151312'};
          background: ${isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'};
        }

        .nav-link.active {
          color: ${isDark ? '#fff' : '#151312'};
          font-weight: 600;
        }

        .nav-actions {
          display: flex;
          align-items: center;
          gap: 12px;
          position: relative;
          z-index: 1002;
        }

        .nav-cta {
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          font-weight: 500;
          color: ${isDark ? '#151312' : '#fff'};
          background: ${isDark ? '#fff' : '#151312'};
          text-decoration: none;
          padding: 8px 16px;
          border-radius: 6px;
          border: 1px solid ${isDark ? '#fff' : '#151312'};
          transition: all 0.2s ease;
        }

        .nav-cta:hover {
          background: ${isDark ? '#f46c38' : '#f46c38'};
          border-color: #f46c38;
          color: #fff;
        }

        .menu-container {
          position: relative;
          z-index: 1003;
        }

        .menu-toggle {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: 6px;
          border: 1px solid ${isDark ? 'rgba(182, 180, 189, 0.2)' : 'rgba(0, 0, 0, 0.1)'};
          background: transparent;
          color: ${isDark ? '#94a3b8' : '#64748b'};
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .menu-toggle:hover {
          color: ${isDark ? '#fff' : '#151312'};
          background: ${isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'};
          border-color: ${isDark ? 'rgba(182, 180, 189, 0.3)' : 'rgba(0, 0, 0, 0.2)'};
        }

        .menu-dropdown {
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          min-width: 200px;
          background: ${isDark ? 'rgba(21, 19, 18, 0.98)' : 'rgba(255, 255, 255, 0.98)'};
          backdrop-filter: blur(12px);
          border: 1px solid ${isDark ? 'rgba(182, 180, 189, 0.15)' : 'rgba(0, 0, 0, 0.1)'};
          border-radius: 10px;
          padding: 8px;
          opacity: 0;
          visibility: hidden;
          transform: translateY(-10px) scale(0.95);
          transform-origin: top right;
          transition: all 0.2s ease;
          box-shadow: 0 8px 32px ${isDark ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.12)'};
          white-space: nowrap;
          z-index: 1001;
          pointer-events: none;
        }

        .menu-dropdown.open {
          opacity: 1;
          visibility: visible;
          transform: translateY(0) scale(1);
          pointer-events: auto;
        }

        .menu-item {
          display: block;
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          font-weight: 500;
          color: ${isDark ? '#94a3b8' : '#64748b'};
          text-decoration: none;
          padding: 10px 14px;
          border-radius: 6px;
          transition: all 0.15s ease;
        }

        .menu-item:hover {
          color: ${isDark ? '#fff' : '#151312'};
          background: ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)'};
        }

        .menu-item.active {
          color: #f46c38;
          background: ${isDark ? 'rgba(244, 108, 56, 0.1)' : 'rgba(244, 108, 56, 0.08)'};
        }

        .theme-toggle {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: 6px;
          border: 1px solid ${isDark ? 'rgba(182, 180, 189, 0.2)' : 'rgba(0, 0, 0, 0.1)'};
          background: transparent;
          color: ${isDark ? '#94a3b8' : '#64748b'};
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .theme-toggle:hover {
          color: ${isDark ? '#fff' : '#151312'};
          background: ${isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'};
          border-color: ${isDark ? 'rgba(182, 180, 189, 0.3)' : 'rgba(0, 0, 0, 0.2)'};
        }

        .soundboard-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: 6px;
          border: 1px solid ${isDark ? 'rgba(182, 180, 189, 0.2)' : 'rgba(0, 0, 0, 0.1)'};
          background: transparent;
          color: ${isDark ? '#94a3b8' : '#64748b'};
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .soundboard-btn:hover {
          color: #f46c38;
          background: ${isDark ? 'rgba(244, 108, 56, 0.1)' : 'rgba(244, 108, 56, 0.08)'};
          border-color: rgba(244, 108, 56, 0.3);
        }

        .soundboard-btn.speaking {
          color: #f46c38;
          animation: pulse 0.5s ease infinite;
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }

        @media (max-width: 900px) {
          .nav-inner {
            padding: 10px 16px;
          }

          .nav-links {
            display: none;
          }

          .nav-cta {
            display: none;
          }
        }

        @media (max-width: 480px) {
          .logo-text {
            font-size: 16px;
          }
        }
      `}</style>
    </nav>
  );
};

export default TopNav;
